//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Button, Grid, Typography } from '@mui/material'
import React, { ReactElement, useEffect } from 'react'

import { theme } from '@react/components/theme'

import { useOtChainState, useOtDispatch, useOtSelector } from '@react/redux/store'
import { EthAddr } from '@shared/SchemaTypes'
import { PoolDynamicOverviewCTO } from '@shared/sharedTypes'
import { poolStateChangeTx, PoolStateChangeEnum } from '@react/signedTxs/poolStateChangeTx'
import { poolDynamicStates } from '@react/fetchers/chainFetchers'
import { logOT } from '@shared/loggingShared'

export default function DynamicSetExchangeRateForm(props: { poolAddr: EthAddr }): ReactElement {
  const { poolAddr } = props

  const {
    chainConfiguration: { providerReadOnly },
  } = useOtChainState()
  const [depositStateActive, setDepositStateActive] = React.useState<boolean>(true)
  const [withdrawStateActive, setWithdrawStateActive] = React.useState<boolean>(true)

  const poolDynamicOverviewCTO = useOtSelector(
    (state) => state.poolState.poolOverviewCTOs[poolAddr] as PoolDynamicOverviewCTO
  )

  useEffect(() => {
    async function fetchDepositState(): Promise<void> {
      if (poolDynamicOverviewCTO?.poolControllerAddr && providerReadOnly) {
        const state = await poolDynamicStates({
          poolControllerAddr: poolDynamicOverviewCTO.poolControllerAddr,
          providerReadOnly,
        })
        setDepositStateActive(state.depositActive)
        setWithdrawStateActive(state.withdrawActive)
      }
    }
    fetchDepositState().catch((e) => {
      console.error('fetchDepositState error', e)
    })

    return () => {
      logOT.info('cleanup')
    }
  }, [poolDynamicOverviewCTO])
  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const dispatch = useOtDispatch()
  const sx = {
    backgroundColor: theme.palette.fg.main,
    color: '#FFFFFF',
    padding: '11px 16px 11px 16px',
    marginLeft: '8px',
    borderRadius: '6px',
    marginTop: 2,
  }
  const activatePoolAction = (): void => {
    setIsSubmitted(true)
    dispatch(poolStateChangeTx({ poolAddr, poolState: PoolStateChangeEnum.ACTIVATE }))
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }
  const pauseDepositAction = (): void => {
    setIsSubmitted(true)
    dispatch(poolStateChangeTx({ poolAddr, poolState: PoolStateChangeEnum.PAUSE_DEPOSITS }))
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }
  const resumeDepositsAction = (): void => {
    setIsSubmitted(true)
    dispatch(poolStateChangeTx({ poolAddr, poolState: PoolStateChangeEnum.RESUME_DEPOSITS }))
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }
  const pauseWithdrawsAction = (): void => {
    setIsSubmitted(true)
    dispatch(poolStateChangeTx({ poolAddr, poolState: PoolStateChangeEnum.PAUSE_WITHDRAWS }))
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }
  const resumeWithdrawsAction = (): void => {
    setIsSubmitted(true)
    dispatch(poolStateChangeTx({ poolAddr, poolState: PoolStateChangeEnum.RESUME_WITHDRAWS }))
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }

  logOT.info('poolDynamicOverviewCTO.state', poolDynamicOverviewCTO.state)
  return (
    <div data-testid="REPAY_EARLY_REDEEM_FORM">
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Typography variant="body" align="left" sx={{ marginBottom: 2 }}>
            Pool State Form
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={6} sm={6}>
        <Button
          disabled={isSubmitted || poolDynamicOverviewCTO.state !== 0}
          sx={sx}
          onClick={() => {
            activatePoolAction()
          }}
          variant={'contained'}
        >
          <Typography variant={'buttonBold'}>Activate Pool</Typography>
        </Button>
      </Grid>
      <Grid item xs={6} sm={6}>
        {depositStateActive && (
          <Button
            disabled={isSubmitted || poolDynamicOverviewCTO.state == 0}
            sx={sx}
            onClick={() => {
              pauseDepositAction()
            }}
            variant={'contained'}
          >
            <Typography variant={'buttonBold'}>Pause Deposits</Typography>
          </Button>
        )}
        {!depositStateActive && (
          <Button
            disabled={isSubmitted || poolDynamicOverviewCTO.state == 0}
            sx={sx}
            onClick={() => {
              resumeDepositsAction()
            }}
            variant={'contained'}
          >
            <Typography variant={'buttonBold'}>Resume Deposits</Typography>
          </Button>
        )}
      </Grid>
      <Grid item xs={6} sm={6}>
        {withdrawStateActive && (
          <Button
            disabled={isSubmitted || poolDynamicOverviewCTO.state == 0}
            sx={sx}
            onClick={() => {
              pauseWithdrawsAction()
            }}
            variant={'contained'}
          >
            <Typography variant={'buttonBold'}>Pause Withdraws</Typography>
          </Button>
        )}
        {!withdrawStateActive && (
          <Button
            disabled={isSubmitted || poolDynamicOverviewCTO.state == 0}
            sx={sx}
            onClick={() => {
              resumeWithdrawsAction()
            }}
            variant={'contained'}
          >
            <Typography variant={'buttonBold'}>Resume Withdraws</Typography>
          </Button>
        )}
      </Grid>
    </div>
  )
}
